import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import {
  FileAssignmentsWarning,
  useFilesToDeleteForCellRanges,
} from "./FileAssignmentsWarning";
import { DataTableStoreState } from "../store/DataTableProvider.types";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";

interface ModalDeleteTableProps {
  table: DataTableStoreState["tables"][number];
  onClose: () => void;
}

export const ModalDeleteTable = ({ table, onClose }: ModalDeleteTableProps) => {
  const files = useFilesToDeleteForCellRanges({ tableId: table.id });
  const deleteTable = useDataTableContext((s) => s.deleteTable);
  const [isLoading, setIsLoading] = useState(false);
  const projectKey = useDataTableContext((s) => s.project.key);

  const tenantKey = useTenantContext((s) => s.currentTenant.key);

  const navigateToProjectReplace = useRouteMapContext(
    (s) =>
      s.routeMap["PROJECT"].dynamicPath({ projectKey, tenantKey })
        .navigateToReplace,
  );
  const handleSubmit = async () => {
    setIsLoading(true);
    const { error } = await deleteTable({
      tableId: table.id,
      fileIds: files.map((f) => f.id),
    });

    setIsLoading(false);

    if (error === undefined) {
      addUtilityToastSuccess("Table deleted");
      navigateToProjectReplace();
    } else {
      addUtilityToastFailure("Failed to delete table");
    }

    onClose();
  };

  const deletionMessage = (() => {
    if (files.length > 0) {
      return table.kind === "data" ? (
        <p>
          <strong>
            Deleting this data table will automatically delete any uploaded
            files assigned to cells in this table unless the file is also
            assigned in at least one other data table
          </strong>{" "}
          (assignments to analysis table inputs are not considered).
        </p>
      ) : (
        <p>
          <strong>
            Deleting this analysis table will automatically delete any output
            files from tasks run in the table.{" "}
          </strong>{" "}
          Input files will <strong>not</strong> be deleted.
        </p>
      );
    } else {
      return table.kind === "data" ? (
        <p>
          This data table does <strong>not</strong> contain any files that will
          be deleted when the table is deleted. Deleting a data table only
          deletes uploaded files that are assigned to cells in the table and not
          assigned in at least one other data table.
        </p>
      ) : (
        <p>
          This analysis table does <strong>not</strong> contain any output files
          from tasks run in the table. Deleting an analysis table only deletes
          output files, input files are not deleted.
        </p>
      );
    }
  })();

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">Delete Table</EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiCallOut
          title="Proceed with caution!"
          color="warning"
          iconType="warning"
        >
          {deletionMessage}
          {files.length > 0 && (
            <>
              <FileAssignmentsWarning files={files} />
              <EuiSpacer size="m" />
            </>
          )}

          <p>
            This action cannot be undone. Any references to cells in this table
            in other data and analysis tables will become invalid.
          </p>
        </EuiCallOut>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          color="danger"
          onClick={() => void handleSubmit()}
        >
          Delete
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
